import type {
  ToolSetEntryCreateArgs,
  ToolSetEntryDeleteArgs,
  ToolSetEntryUpdateArgs,
  ToolSetUpdateArgs,
} from '@erikdakoda/database/types';
import { getNewToolSetEntry } from '@erikdakoda/tool-set/getNewToolSetEntry';
import type {
  ToolSetEntryDraft,
  ToolSetEntryForEdit,
  ToolSetForEdit,
} from '@erikdakoda/tool-set/types';

export type { ToolSetEntryDraft };

/** Strips deferred keys that are also disabled (disabled wins). */
export function sanitizeEntryDraft(entry: ToolSetEntryDraft): ToolSetEntryDraft {
  const disabled = new Set(entry.disabledTools);
  return {
    ...entry,
    deferredTools: entry.deferredTools.filter((k) => !disabled.has(k)),
  };
}

export function entriesFromToolSet(
  entries: ToolSetEntryForEdit[] | undefined,
): ToolSetEntryDraft[] {
  return (entries ?? []).map((entry) => ({
    id: entry.id,
    kind: entry.kind as ToolSetEntryDraft['kind'],
    key: entry.key,
    disabledTools: [...(entry.disabledTools ?? [])],
    deferredTools: [...(entry.deferredTools ?? [])],
    deferred: entry.deferred ?? false,
  }));
}

function isFullEntryDraftArray(changes: unknown[]): changes is ToolSetEntryDraft[] {
  return changes.every(
    (entry) =>
      entry !== null &&
      typeof entry === 'object' &&
      !Array.isArray(entry) &&
      typeof (entry as ToolSetEntryDraft).kind === 'string' &&
      typeof (entry as ToolSetEntryDraft).key === 'string',
  );
}

/** Merges Editor `differences()` partial entry patches with the loaded baseline rows. */
export function resolveEntryDraftsForSave(
  baseline: ToolSetEntryDraft[],
  changes: unknown,
): ToolSetEntryDraft[] {
  if (changes === undefined || changes === null) {
    return baseline;
  }
  if (!Array.isArray(changes)) {
    return baseline;
  }
  // Full drafts from the control (including same-length remove+add) replace baseline as-is.
  if (isFullEntryDraftArray(changes)) {
    return changes;
  }
  if (changes.length !== baseline.length) {
    return changes as ToolSetEntryDraft[];
  }

  return baseline.map((entry, index) => {
    const patch = changes[index];
    if (patch === null || typeof patch !== 'object' || Array.isArray(patch)) {
      return entry;
    }
    const patchObj = patch as Partial<ToolSetEntryDraft>;
    if (Object.keys(patchObj).length === 0) {
      return entry;
    }
    return { ...entry, ...patchObj };
  });
}

function entryKindKey(entry: Pick<ToolSetEntryDraft, 'kind' | 'key'>): string {
  return `${entry.kind}:${entry.key}`;
}

export type UpdateToolSetArgs = {
  toolSet: Pick<ToolSetForEdit, 'id' | 'ownerId' | 'spaceId'>;
  updateArgs: ToolSetUpdateArgs;
  entries: ToolSetEntryDraft[];
  updateToolSet: (args: ToolSetUpdateArgs) => Promise<unknown>;
  createToolSetEntry: (args: ToolSetEntryCreateArgs) => Promise<unknown>;
  updateToolSetEntry: (args: ToolSetEntryUpdateArgs) => Promise<unknown>;
  deleteToolSetEntry: (args: ToolSetEntryDeleteArgs) => Promise<unknown>;
  existingEntryIds: string[];
  /** Loaded entries before save; used to resolve partial diffs and existing kind/key rows. */
  baselineEntries: ToolSetEntryDraft[];
};

/** Saves toolSet header fields and reconciles ToolSetEntry rows. */
export async function updateToolSetAndEntries(args: UpdateToolSetArgs): Promise<void> {
  await args.updateToolSet(args.updateArgs);

  const keptIds = new Set<string>();
  const existingIdByKindKey = new Map(
    args.baselineEntries
      .filter((entry): entry is ToolSetEntryDraft & { id: string } => entry.id !== undefined)
      .map((entry) => [entryKindKey(entry), entry.id] as const),
  );

  for (const entry of args.entries) {
    const data = sanitizeEntryDraft(entry);
    const entryId = entry.id ?? existingIdByKindKey.get(entryKindKey(data));
    if (entryId) {
      keptIds.add(entryId);
      await args.updateToolSetEntry({
        where: { id: entryId },
        data: {
          kind: data.kind,
          key: data.key,
          disabledTools: data.disabledTools,
          deferredTools: data.deferredTools,
          deferred: data.deferred,
        },
      });
    } else {
      await args.createToolSetEntry({
        data: getNewToolSetEntry(args.toolSet, data),
      });
    }
  }

  for (const existingId of args.existingEntryIds) {
    if (!keptIds.has(existingId)) {
      await args.deleteToolSetEntry({ where: { id: existingId } });
    }
  }
}
